import React from "react";
import "../styles/LawAtAGlance.css";

/*
 * LawAtAGlance
 * A one-line status strip for country pages: four legal markers, each a dot + one word.
 * Placement: full width, below the "Plan your ... Trip" / "The Queer Landscape" columns, above "LGBTQ+ Organisations".
 *
 * Usage:
 *   <LawAtAGlance status={lawStatusFromCountry(country)} />
 *   The values are edited per country in the dashboard (Countries).
 *
 * If a country has no entry, or a marker value is null, that marker is not shown.
 * If no marker has a value, the strip is not rendered at all.
 */

// dot: "full" | "half" | "empty"
const MARKERS = [
  {
    key: "relations",
    label: "Relations",
    values: {
      legal: { text: "Legal", dot: "full" },
      criminalized: { text: "Criminalized", dot: "empty" },
    },
  },
  {
    key: "marriage",
    label: "Marriage",
    values: {
      yes: { text: "Yes", dot: "full" },
      partnership: { text: "Partnership", dot: "half" },
      no: { text: "No", dot: "empty" },
    },
  },
  {
    key: "protection",
    label: "Protection",
    values: {
      yes: { text: "Yes", dot: "full" },
      partly: { text: "Partly", dot: "half" },
      no: { text: "No", dot: "empty" },
    },
  },
  {
    key: "genderId",
    label: "Gender on ID",
    values: {
      yes: { text: "Yes", dot: "full" },
      restricted: { text: "Restricted", dot: "half" },
      no: { text: "No", dot: "empty" },
    },
  },
];

function formatChecked(checked) {
  // expects "YYYY-MM", shows e.g. "Oct 2026"
  if (!checked) return null;
  const [y, m] = checked.split("-").map(Number);
  if (!y || !m) return null;
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });
}

/** Values and dropdown options, shared with the dashboard editor. */
export const LAW_MARKERS = MARKERS;

/** The component's status object from a countries row (law_* columns). */
export function lawStatusFromCountry(c) {
  if (!c) return null;
  return {
    relations: c.law_relations || null,
    marriage: c.law_marriage || null,
    protection: c.law_protection || null,
    genderId: c.law_gender_id || null,
    checked: c.law_checked || null,
  };
}

export default function LawAtAGlance({ status }) {
  if (!status) return null;

  const items = MARKERS.map((m) => {
    const v = m.values[status[m.key]];
    return v ? { key: m.key, label: m.label, ...v } : null;
  }).filter(Boolean);

  if (items.length === 0) return null;

  const checked = formatChecked(status.checked);

  return (
    <section className="law-glance" aria-label="The law at a glance">
      <span className="law-glance__title">The law at a glance</span>
      <ul className="law-glance__list">
        {items.map((i) => (
          <li key={i.key} className="law-glance__item">
            <span
              className={`law-glance__dot law-glance__dot--${i.dot}`}
              aria-hidden="true"
            />
            <span>
              {i.label}: <strong>{i.text}</strong>
            </span>
          </li>
        ))}
      </ul>
      {checked && (
        <span className="law-glance__checked">
          Checked {checked}
        </span>
      )}
    </section>
  );
}
